<script setup>
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  cameraList: { type: Array, default: () => [] }
})

const targetElement = ref(null)
const sliderValue = ref(0)
const maxIndex = computed(() => props.cameraList.length - 1)
const { clearId, onClickPlay, onClickStop } = useAnimate(sliderValue, maxIndex)
const imgUrl = computed(() => props.cameraList[sliderValue.value]?.url || '')
const date = computed(() => props.cameraList[sliderValue.value]?.date
  ? dayjs.unix(props.cameraList[sliderValue.value].date).tz('Asia/Tokyo').format('YYYY.M.D H:mm')
  : ''
)

onMounted(async () => {
  const observerOptions = {
    root: null,
    threshold: 1
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        onClickPlay()
      } else {
        onClickStop()
      }
    })
  }, observerOptions)

  if (targetElement.value) {
    observer.observe(targetElement.value)
  }
  onUnmounted(() => {
    if (targetElement.value) {
      observer.unobserve(targetElement.value)
    }
    clearTimeout(clearId.value)
  })
})
</script>

<template>
  <section class="riverDetail">
    <div class="content">
      <div class="title">
        <p class="text">
          現在の様子（ライブカメラ）
        </p>
      </div>
      <div>
        <div
          v-if="(props.cameraList &&props.cameraList.length > 1)"
          ref="targetElement"
          class="image"
        >
          <img
            :src="imgUrl"
            alt=""
          >
          <p>{{ date }}</p>
        </div>
        <div
          v-else
          class="no-image"
        >
          <p class="no-image-text">
            カメラ画像が取得できませんでした。
          </p>
        </div>
        <RiverCameraControl
          v-model:slider-value="sliderValue"
          :camera-list="props.cameraList"
        />
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.riverDetail {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .head {
    display: flex;
    flex-direction: column;
    gap: 5px;
    align-items: flex-start;
  }
  .tag {
    padding: 6px 10px;
    border-radius: 4px;
    background-color: #EEF1F8;
    color: #606060;
    font-size: 10px;
    font-weight: 600;
    line-height: 1em;
    letter-spacing: .25px;
  }
  .content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    .title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .text {
        padding-left: 8px;
        border-left: solid 2px #0C419A;
        color: #303030;
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
      }
      .date {
        color: #606060;
        font-size: 12px;
        font-weight: 300;
        line-height: 1em;
      }
    }
    .image {
      position: relative;
      width: 100%;
      max-height: calc((100vw - 20px) *9 / 16);
      aspect-ratio: 16 / 9;
      background-color: #303030;;
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      p {
        position: absolute;
        top: 6px;
        right: 6px;
        padding: 5px 10px;
        border-radius: 4px;
        color: #303030;
        background-color: rgb(238 241 248 / .8);
        box-shadow: 0 1px 2px 0 rgb(53 105 192 / .4);
        font-size: 12px;
        font-weight: 300;
        line-height: 1em;
        letter-spacing: .25px;
      }
    }
    .no-image {
      width: 100%;
      aspect-ratio: 16 / 9;
      background-color: #f6f6f6;
      .no-image-text {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        color: #ff3f00;
        font-size: 14px;
        font-weight: 600;
        line-height: 1em;
      }
    }
  }
  @media (any-hover: hover) {
  }
}
@media (min-width: 830px) {
  .riverDetail {
    .content {
      .title {
        .text {
          font-size: 18px;
          line-height: 22px;
        }
        .date {
          font-size: 14px;
        }
      }
      .image {
        max-height: calc(740px *9 / 16);
        p {
          font-size: 14px;
          font-weight: 600;
        }
      }
      .no-image {
        .no-image-text {
          font-size: 18px;
        }
      }
    }
  }
}
</style>
